import { useState } from "react";
import { BoardAction, createLink } from "./boardReducer";
import { BoardLink, BoardState } from "../domain/types";

interface UseBoardLinksArgs {
    state: BoardState;
    dispatch: (action: BoardAction) => void;
    selectedId: string | null;
    setSelectedId: (id: string | null) => void;
    /** Called when link mode starts, so the page can close other editors. */
    onEnterLinkMode?: () => void;
}

export interface BoardLinks {
    /** Figure the pending link starts from, or null when not linking. */
    linkingFromId: string | null;
    startLink: (id: string) => void;
    linkTarget: (targetId: string) => void;
    cancelLinking: () => void;
    removeLink: (id: string) => void;
    openLinkEdit: (id: string) => void;
    /** Link currently open in the editor, or null. */
    editingLink: BoardLink | null;
    closeLinkEditor: () => void;
}

/**
 * Link interaction state machine shared by the therapist and patient boards:
 * pick a source figure, pick a target, create the link and open its editor.
 *
 * Both ids are derived against the live board state, so a figure or link that
 * disappears (locally or via a remote edit) cannot leave the UI stuck in
 * linking mode or with a dangling editor open.
 */
export function useBoardLinks({
    state,
    dispatch,
    selectedId,
    setSelectedId,
    onEnterLinkMode,
}: UseBoardLinksArgs): BoardLinks {
    const [linkingFrom, setLinkingFrom] = useState<string | null>(null);
    const [editingLinkId, setEditingLinkId] = useState<string | null>(null);

    const linkingFromId =
        linkingFrom && state.puppets.some((p) => p.id === linkingFrom) ? linkingFrom : null;
    const editingLink = (state.links ?? []).find((l) => l.id === editingLinkId) ?? null;

    const startLink = (id: string) => {
        onEnterLinkMode?.();
        // Tapping the same figure again leaves link mode.
        setLinkingFrom((cur) => (cur === id ? null : id));
    };

    const linkTarget = (targetId: string) => {
        if (!linkingFromId || targetId === linkingFromId) return;
        const link = createLink(linkingFromId, targetId);
        dispatch({ type: "addLink", link });
        setLinkingFrom(null);
        setSelectedId(link.id);
        setEditingLinkId(link.id);
    };

    const cancelLinking = () => setLinkingFrom(null);

    const removeLink = (id: string) => {
        dispatch({ type: "removeLink", id });
        if (selectedId === id) setSelectedId(null);
        if (editingLinkId === id) setEditingLinkId(null);
    };

    const openLinkEdit = (id: string) => {
        setSelectedId(id);
        setEditingLinkId(id);
    };

    return {
        linkingFromId,
        startLink,
        linkTarget,
        cancelLinking,
        removeLink,
        openLinkEdit,
        editingLink,
        closeLinkEditor: () => setEditingLinkId(null),
    };
}
